import React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { FC, KeyboardEvent } from 'react';
import { Button } from '../../ui/Button/Button';
import { BackButton } from '../../ui/BackButton/BackButton';
import { IconDownload, IconPencil, IconSpinner, InfoIcon } from '../../../assets/icons/icons';
import { STATUS_META } from '../../../data/projectsTable';
import type { ProjectStatus } from '../../../types/project';
import { cx } from '../../../utils/cx';
import { formatDateTime } from '../../../utils/format';
import styles from './ProjectHeader.module.scss';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import { ProjectNotesWindow } from '../ProjectNotes/ProjectNotesWindow';
import notesStyles from '../ProjectNotes/ProjectNotesWindow.module.scss';
import { useProjectNote } from '../../../entities/project/model/useProjectNote';

const IconNote: FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true">
        <path d="M4 1.75h6.5L13 4.25v10H4a1 1 0 0 1-1-1v-10.5a1 1 0 0 1 1-1z" />
        <path d="M5.5 6.5h5M5.5 9h5M5.5 11.5h3" />
    </svg>
);

export interface BranchesProgress {
    left: number;
    total: number;
}

interface ProjectHeaderProps {
    title: string;
    status: ProjectStatus | null;
    branches?: BranchesProgress | null;
    createdAt?: string;
    author?: string;
    projectId: string;
    onRename: (title: string) => Promise<boolean>;
    onDownloadTemplate: () => void;
}

const branchesLabel = ({ left, total }: BranchesProgress): string => {
    const base = 'ищем аналоги и предложения площадок';
    if (left <= 0) return base;
    return total > 0 ? `${base} — осталось ${left} из ${total}` : `${base} — осталось ${left}`;
};

export const ProjectHeader: FC<ProjectHeaderProps> = ({
    title,
    status,
    branches = null,
    createdAt,
    author,
    projectId,
    onRename,
    onDownloadTemplate,
}) => {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState(title);
    const [metaOpen, setMetaOpen] = useState(false);
    const [notesOpen, setNotesOpen] = useState(false);

    const note = useProjectNote(projectId);

    useEffect(() => {
        setNotesOpen(false);
    }, [projectId]);

    const inputRef = useRef<HTMLInputElement>(null);
    const metaRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editing]);

    useEffect(() => {
        if (!metaOpen) return undefined;

        const onPointerDown = (event: MouseEvent) => {
            if (!metaRef.current?.contains(event.target as Node)) setMetaOpen(false);
        };
        const onKey = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape') setMetaOpen(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKey);

        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [metaOpen]);

    const startEdit = () => {
        setDraft(title);
        setEditing(true);
    };

    const save = async (): Promise<void> => {
        setSaving(true);
        const ok = await onRename(draft.trim());
        setSaving(false);

        if (ok) setEditing(false);
        else inputRef.current?.focus();
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            void save();
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            setEditing(false);
        }
    };

    return (
        <div className={styles.head}>
            <BackButton />

            {editing ? (
                <>
                    <input
                        ref={inputRef}
                        className={styles.titleInput}
                        maxLength={120}
                        aria-label="Наименование проекта"
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={onKeyDown}
                    />
                    <Button variant="primary" size="sm" disabled={saving} onClick={() => void save()}>
                        {saving ? 'Сохранение…' : 'Сохранить'}
                    </Button>
                    <Button size="sm" disabled={saving} onClick={() => setEditing(false)}>
                        Отмена
                    </Button>
                </>
            ) : (
                <>
                    <div className={styles.titleWrap}>
                        <h1 onDoubleClick={startEdit}>{title}</h1>
                        <Tooltip text="Переименовать проект" align="right">
                            <button
                                type="button"
                                className={styles.renameBtn}
                                aria-label="Переименовать проект"
                                onClick={startEdit}
                            >
                                <IconPencil />
                            </button>
                        </Tooltip>
                    </div>

                    {status ? (
                        <span className={cx(styles.status, styles[status])}>
                            {STATUS_META[status].label}
                        </span>
                    ) : (
                        <span className={styles.statusEmpty}> </span>
                    )}

                    {branches && (
                        <span className={styles.branches} role="status">
                            <IconSpinner className={styles.branchesSpinner} aria-hidden="true" />
                            {branchesLabel(branches)}
                        </span>
                    )}

                    <div className={styles.meta} ref={metaRef}>
                        <button
                            type="button"
                            className={styles.metaBtn}
                            aria-label="Сведения о проекте"
                            aria-expanded={metaOpen}
                            onClick={() => setMetaOpen((value) => !value)}
                        >
                            <InfoIcon />
                        </button>

                        {metaOpen && (
                            <dl className={styles.metaPop}>
                                <div>
                                    <dt>Создан</dt>
                                    <dd>{createdAt ? formatDateTime(new Date(createdAt)) : '—'}</dd>
                                </div>
                                <div>
                                    <dt>Заказчик</dt>
                                    <dd>{author || '—'}</dd>
                                </div>
                                <div>
                                    <dt>ID</dt>
                                    <dd>{projectId}</dd>
                                </div>
                            </dl>
                        )}
                    </div>
                </>
            )}

            <div className={styles.spacer} />

            <div className={styles.actions}>
                <Button
                    icon={<IconNote className={notesStyles.btnIcon} />}
                    onClick={() => setNotesOpen((value) => !value)}
                >
                    Заметки
                </Button>
                <Button variant="excel" icon={<IconDownload />} onClick={onDownloadTemplate}>
                    Скачать шаблон заявки
                </Button>
            </div>

            <ProjectNotesWindow
                open={notesOpen}
                projectTitle={title}
                note={note}
                onClose={() => setNotesOpen(false)}
            />
        </div>
    );
};